import { Controller } from "@hotwired/stimulus";
import { FetchRequest } from '@rails/request.js';
import subscribeToUserCreation from "../channels/user_creation_channel";

/**
 * Stimulus controller for user registration and ActionCable subscription.
 * Handles user creation, retry logic, and error display.
 *
 * @class RegistrationController
 * @extends Controller
 */
export default class extends Controller {
  /**
   * URL and UID values passed from the DOM.
   * @type {{url: String, uid: String}}
   */
  static values = {
    url: String,
    uid: String
  }

  /**
   * Content target for displaying error messages.
   * @type {string[]}
   */
  static targets = ["content"]

  /**
   * Called when the controller is connected to the DOM.
   * Subscribes to the user creation ActionCable channel and triggers user creation.
   */
  connect() {
    console.log('Registration controller connected with URL:', this.urlValue, 'and UID:', this.uidValue);
    // Subscribe to the user creation ActionCable channel if UID is present
    if (this.hasUidValue) {
      // Pass callback to wait for connection confirmation before creating user
      this.subscription = subscribeToUserCreation(this.uidValue, () => {
        console.log('ActionCable subscription confirmed, now creating user');
        this.createUser();
      });
      console.log('Subscribed to user creation channel with UID:', this.uidValue);
    } else {
      // If no UID, create user immediately
      this.createUser();
    }
  }

  /**
   * Attempts to create a user via FetchRequest. Retries up to 3 times on failure, then shows an error.
   * Redirects are handled by ActionCable subscription.
   */
  async createUser() {
    const request = new FetchRequest('post', this.urlValue);
    const response = await request.perform();
    if (response.ok) {
      console.log('User created successfully');
      // Redirect will be handled by ActionCable subscription
    } else {
      // Increment retry count in localStorage
      localStorage.setItem('count', +(localStorage.getItem('count')) + 1);
      if (+(localStorage.getItem('count')) < 3) {
        // Retry after 10 seconds if fewer than 3 attempts
        setTimeout(function () {
          location.reload();
        }, 10000);
      } else {
        // Show error message and reset retry count
        this.contentTarget.classList = 'mx-4 d-flex flex-column align-items-start';
        this.contentTarget.innerHTML = "Something went wrong. <br><a class='link link--live' href='mailto:help@acupoll.com'>Click here to message a moderator</a>";
        localStorage.setItem('count', 0);
      }
    }
  }

  /**
   * Called when the controller is disconnected from the DOM.
   * Cleans up the ActionCable subscription.
   */
  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }
  }
}